import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BUILT_IN_ROLE_DEFINITIONS,
  USER_STATUSES,
  USER_STATUS_LABELS,
  canManageUser,
  daysBetween,
  formatDate,
  toIsoDate,
  type Paginated,
  type RoleOptionsResponse,
  type UserRole,
  type UserStatus,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { Alert, Badge, Card, Input, Select, Skeleton, Table, Td, Th, Tr } from '@/components/ui';
import { EmptyState, PageHeader, Pagination } from '@/components/domain';
import { cn } from '@/lib/cn';
import type { UserItem } from '@/types';

const LIMIT = 50;

/** PRD par. 2.1: en aktiv bruger der ikke har vaeret logget ind i 30 dage skal falde i oejnene. */
const STALE_LOGIN_DAYS = 30;

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const { user: actor, can } = useAuth();
  /* 'Se brugere' uden 'Ændr brugere': listen til at laese. */
  const canEdit = can('users.manage');
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', { search, roleFilter, statusFilter, offset }],
    queryFn: () =>
      api.get<Paginated<UserItem>>('/users', {
        search: search || undefined,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
        include_last_login: true,
        limit: LIMIT,
        offset,
      }),
    /**
     * Overstyrer standarden (30 sekunders staleTime, ingen refetch ved fokus).
     *
     * Roller og status redigeres ogsaa direkte i databasen, og en admin der retter en
     * raekke i SQL-editoren og skifter tilbage til fanen skal se det med det samme - ikke
     * efter en tilfaeldig cache-udloebstid.
     */
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  /*
   * The roles a user can be given - the built-in and the ones an admin created on /admin/roller.
   * The built-in until the list is there, so the dropdowns are never empty.
   */
  const { data: roleOptions } = useQuery({
    queryKey: ['roles', 'options'],
    queryFn: () => api.get<RoleOptionsResponse>('/roles/options'),
  });
  const roles = roleOptions?.roles ?? BUILT_IN_ROLE_DEFINITIONS;

  const refresh = (): Promise<unknown> => queryClient.invalidateQueries({ queryKey: ['users'] });

  const changeRole = useMutation({
    mutationFn: (input: { id: string; role: UserRole }) =>
      api.put(`/users/${input.id}/role`, { role: input.role }),
    onSuccess: refresh,
  });

  const changeStatus = useMutation({
    mutationFn: (input: { id: string; status: UserStatus }) =>
      api.put(`/users/${input.id}/status`, { status: input.status }),
    onSuccess: refresh,
  });

  const rowError = changeRole.error ?? changeStatus.error;
  const today = toIsoDate(new Date());

  return (
    <>
      <PageHeader
        title="Brugere"
        description={
          canEdit
            ? 'Rolle og status kan ændres direkte i listen. Brugere oprettes af en administrator.'
            : 'Brugerne med rolle, status og sidste login. Din rolle kan se listen, men ikke ændre den.'
        }
      />

      {error ? (
        <Alert tone="error" className="mb-4">
          {error instanceof ApiError ? error.message : 'Kunne ikke hente brugerne.'}
        </Alert>
      ) : null}

      {rowError ? (
        <Alert
          tone="error"
          className="mb-4"
          // Fejlen findes kun som mutationstilstand. Begge nulstilles: kun én er sat, og
          // et reset paa en mutation uden fejl er en no-op.
          onDismiss={() => {
            changeRole.reset();
            changeStatus.reset();
          }}
        >
          {rowError instanceof ApiError ? rowError.message : 'Handlingen fejlede.'}
        </Alert>
      ) : null}

      <Card>
        <div className="flex flex-wrap gap-3 border-b border-line p-3">
          <Input
            className="max-w-xs"
            placeholder="Søg på navn eller email"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setOffset(0);
            }}
          />
          <Select
            className="w-44"
            value={roleFilter}
            onChange={(event) => {
              setRoleFilter(event.target.value);
              setOffset(0);
            }}
          >
            <option value="">Alle roller</option>
            {roles.map((option) => (
              <option key={option.role} value={option.role}>
                {option.label}
              </option>
            ))}
          </Select>
          <Select
            className="w-40"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setOffset(0);
            }}
          >
            <option value="">Alle statusser</option>
            {USER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {USER_STATUS_LABELS[status]}
              </option>
            ))}
          </Select>
        </div>

        {isLoading ? (
          <Skeleton className="m-4 h-32" />
        ) : (data?.data.length ?? 0) === 0 ? (
          <EmptyState title="Ingen brugere matcher filtrene" />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Navn</Th>
                  <Th>Email</Th>
                  <Th className="w-[180px]">Jobrolle</Th>
                  <Th className="w-[150px]">Status</Th>
                  <Th className="w-[170px]">Sidst logget ind</Th>
                </tr>
              </thead>
              <tbody>
                {data?.data.map((item) => {
                  const inactive = item.status === 'Inactive';
                  const isSelf = item.id === actor?.id;
                  /* En admin kan kun aendres af en admin - se `canManageUser`. */
                  const editable =
                    canEdit && Boolean(actor && canManageUser(actor.role, item.role));

                  /**
                   * "30+ dage" kraever en dato at regne fra. En bruger der aldrig har
                   * logget ind har ingen, og badget ville paastaa noget der ikke er maalt.
                   * Badget vises kun for aktive brugere: for en deaktiveret er det
                   * forventet at der ikke har vaeret logins.
                   */
                  const staleDays = item.last_login_at
                    ? daysBetween(item.last_login_at.slice(0, 10), today)
                    : null;
                  const stale = staleDays !== null && staleDays >= STALE_LOGIN_DAYS;

                  return (
                    <Tr key={item.id} className={cn(inactive && 'bg-canvas text-ink-muted')}>
                      <Td className={cn(inactive && 'line-through')}>
                        {item.display_name ?? item.email}
                      </Td>
                      <Td className="text-xs">{item.email}</Td>
                      <Td>
                        <Select
                          className="h-8 text-xs"
                          value={item.role ?? ''}
                          // En admin maa ikke degradere sig selv - det kan laase alle ude.
                          disabled={!editable || isSelf || changeRole.isPending}
                          onChange={(event) =>
                            changeRole.mutate({
                              id: item.id,
                              role: event.target.value as UserRole,
                            })
                          }
                        >
                          {/* Kun synlig for en bruger der endnu ikke har faaet en rolle. */}
                          {item.role === null ? <option value="">Ingen rolle</option> : null}
                          {/* A role the list does not know - deleted since - still shows its name. */}
                          {item.role && !roles.some((option) => option.role === item.role) ? (
                            <option value={item.role}>{item.role_label ?? item.role}</option>
                          ) : null}
                          {roles.map((option) => (
                            <option
                              key={option.role}
                              value={option.role}
                              /* Kun en admin kan goere andre til admin. */
                              disabled={Boolean(
                                actor && !canManageUser(actor.role, null, option.role),
                              )}
                            >
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </Td>
                      <Td>
                        <Select
                          className="h-8 text-xs"
                          value={item.status}
                          disabled={!editable || isSelf || changeStatus.isPending}
                          onChange={(event) =>
                            changeStatus.mutate({
                              id: item.id,
                              status: event.target.value as UserStatus,
                            })
                          }
                        >
                          {USER_STATUSES.map((status) => (
                            <option key={status} value={status}>
                              {USER_STATUS_LABELS[status]}
                            </option>
                          ))}
                        </Select>
                      </Td>
                      <Td className="text-xs tabular">
                        {formatDate(item.last_login_at)}
                        {!inactive && stale ? (
                          <Badge tone="warning" className="ml-1">
                            30+ dage
                          </Badge>
                        ) : null}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
            <Pagination
              total={data?.total ?? 0}
              limit={LIMIT}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>
    </>
  );
}
